import React, { useState, useEffect, useRef, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { X, Sparkles, MessageSquare, Mic, Camera, PenLine, Compass, BarChart3, BookOpen, TrendingUp, ChevronRight } from 'lucide-react';
import { FeatureDiscoveryState, Language } from '../types';
import { STEP_MAP } from '../contexts/FeatureDiscoveryContext';

interface FeatureDiscoveryProps {
  featureKey: keyof FeatureDiscoveryState;
  discoveryState: FeatureDiscoveryState;
  onDismiss: (key: keyof FeatureDiscoveryState) => void;
  onActivate?: (key: keyof FeatureDiscoveryState) => void;
  onNavigate?: (screen: 'pulse' | 'archive' | 'consult' | 'decisions' | 'insights') => void;
  currentView?: 'pulse' | 'archive' | 'consult' | 'decisions' | 'insights';
  language: Language;
  position?: 'top' | 'bottom' | 'center';
  showArrow?: boolean;
  arrowDirection?: 'up' | 'down' | 'left' | 'right';
}

const viewMapping: Record<string, 'pulse' | 'archive' | 'consult' | 'decisions' | 'insights'> = {
  home: 'pulse',
  journal: 'archive',
  chat: 'consult',
  decisions: 'decisions',
  insights: 'insights'
};

const DISCOVERY_CONTENT: Record<Language, Partial<Record<keyof FeatureDiscoveryState, {
  title: string;
  description: string;
  icon: React.ReactNode;
  ctaText: string;
}>>> = {
  Italian: {
    pulseIntro: {
      title: "Esplora. Cresce con te.",
      description: "Questa app impara a conoscerti giorno dopo giorno. Più condividi, più diventa intelligente. Iniziamo insieme questo viaggio.",
      icon: <Sparkles size={20} className="text-indigo-500" />,
      ctaText: "Iniziamo"
    },
    evolutionExplained: {
      title: "Cattura un momento",
      description: "Scrivi come ti senti, cosa è successo, o cosa stai pensando.\n\nEs: \"Oggi mi sento pieno di energia\" oppure \"Ho litigato con Marco e mi sento confuso\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Scrivi qualcosa"
    },
    homeInputDiscovered: {
      title: "Cattura un momento",
      description: "Scrivi come ti senti, cosa è successo, o cosa stai pensando.\n\nEs: \"Oggi mi sento pieno di energia\" oppure \"Ho litigato con Marco e mi sento confuso\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Scrivi qualcosa"
    },
    voiceInputDiscovered: {
      title: "Preferisci parlare?",
      description: "Tieni premuto il microfono e racconta. La tua voce verrà trascritta automaticamente.",
      icon: <Mic size={20} className="text-rose-500" />,
      ctaText: "Prova la voce"
    },
    photoInputDiscovered: {
      title: "Aggiungi una foto",
      description: "Un'immagine può raccontare molto. Scatta o scegli una foto per arricchire il tuo momento.",
      icon: <Camera size={20} className="text-emerald-500" />,
      ctaText: "Scatta una foto"
    },
    archiveIntro: {
      title: "Ecco il tuo Diario",
      description: "Tutti i tuoi momenti vengono salvati qui. Puoi rivederli, cercarli e scoprire pattern nel tempo.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Capito, continua"
    },
    journalDiscovered: {
      title: "Il tuo archivio personale",
      description: "Qui trovi tutto ciò che hai tracciato. Ora scopriamo la chat.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Vai alla chat"
    },
    consultIntro: {
      title: "Parla con me",
      description: "Qui puoi conversare con l'AI che ti conosce. Chiedi consigli, esplora decisioni, o semplicemente rifletti insieme.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Capito"
    },
    chatDiscovered: {
      title: "La tua chat personale",
      description: "Qui puoi chiedere consigli, esplorare decisioni e riflettere insieme all'AI che ti conosce. Torna quando vuoi parlare.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Vai alle decisioni"
    },
    decisionsIntro: {
      title: "Le tue Decisioni",
      description: "Qui puoi tracciare le scelte importanti della tua vita. L'AI ti aiuterà ad analizzarle.",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Capito"
    },
    decisionsDiscovered: {
      title: "Le tue decisioni",
      description: "Qui puoi tracciare le scelte importanti. L'AI ti aiuterà ad analizzarle quando ne avrai bisogno.\n\nEs: \"Devo accettare il nuovo lavoro?\"",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Continua"
    },
    insightsIntro: {
      title: "I tuoi Insight",
      description: "Man mano che tracci, l'AI scopre pattern nel tuo comportamento. Qui vedrai la tua evoluzione.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Capito"
    },
    insightsDiscovered: {
      title: "Scopri i tuoi pattern",
      description: "Qui vedrai come stai evolvendo nel tempo. Emozioni, decisioni, valori - tutto diventa chiaro.\n\nNella zona sintesi puoi anche aggiungere le persone a te care - l'AI le riconoscerà man mano che le menzioni.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Inizia ad esplorare"
    },
    valuesDiscovered: {
      title: "I tuoi valori",
      description: "Cosa conta davvero per te? Aggiungi almeno un valore che guida le tue scelte.\n\nEs: \"Famiglia\", \"Crescita\", \"Libertà\"\n\nPotrai sempre modificarli e aggiungerne altri nel tempo.",
      icon: <Sparkles size={20} className="text-emerald-500" />,
      ctaText: "Aggiungi un valore"
    },
    goalsDiscovered: {
      title: "I tuoi obiettivi",
      description: "Verso cosa stai lavorando? Aggiungi almeno un obiettivo che vuoi raggiungere.\n\nEs: \"Migliorare la salute\", \"Nuovo lavoro\"\n\nPotrai sempre modificarli e aggiungerne altri nel tempo.",
      icon: <TrendingUp size={20} className="text-blue-500" />,
      ctaText: "Aggiungi un obiettivo"
    },
    allDiscoveryComplete: {
      title: "",
      description: "",
      icon: null,
      ctaText: ""
    }
  },
  English: {
    pulseIntro: {
      title: "Explore. It grows with you.",
      description: "This app learns to know you day by day. The more you share, the smarter it becomes. Let's start this journey together.",
      icon: <Sparkles size={20} className="text-indigo-500" />,
      ctaText: "Let's start"
    },
    evolutionExplained: {
      title: "Capture a moment",
      description: "Write how you feel, what happened, or what you're thinking about.\n\nE.g.: \"I feel full of energy today\" or \"I argued with Marco and I feel confused\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Write something"
    },
    homeInputDiscovered: {
      title: "Capture a moment",
      description: "Write how you feel, what happened, or what you're thinking about.\n\nE.g.: \"I feel full of energy today\" or \"I argued with Marco and I feel confused\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Write something"
    },
    voiceInputDiscovered: {
      title: "Prefer to speak?",
      description: "Hold the microphone and share. Your voice will be transcribed automatically.",
      icon: <Mic size={20} className="text-rose-500" />,
      ctaText: "Try voice"
    },
    photoInputDiscovered: {
      title: "Add a photo",
      description: "An image can tell a lot. Take or choose a photo to enrich your moment.",
      icon: <Camera size={20} className="text-emerald-500" />,
      ctaText: "Take a photo"
    },
    archiveIntro: {
      title: "Here's your Journal",
      description: "All your moments are saved here. You can review them, search them, and discover patterns over time.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Got it, continue"
    },
    journalDiscovered: {
      title: "Your personal archive",
      description: "Here you'll find everything you've tracked. Now let's discover the chat.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Go to chat"
    },
    consultIntro: {
      title: "Talk to me",
      description: "Here you can chat with the AI that knows you. Ask for advice, explore decisions, or simply reflect together.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Got it"
    },
    chatDiscovered: {
      title: "Your personal chat",
      description: "Here you can ask for advice, explore decisions and reflect together with the AI that knows you. Come back whenever you want to talk.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Go to decisions"
    },
    decisionsIntro: {
      title: "Your Decisions",
      description: "Here you can track the important choices in your life. The AI will help you analyze them.",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Got it"
    },
    decisionsDiscovered: {
      title: "Your decisions",
      description: "Here you can track important choices. The AI will help you analyze them when you need.\n\nE.g.: \"Should I accept the new job?\"",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Continue"
    },
    insightsIntro: {
      title: "Your Insights",
      description: "As you track, the AI discovers patterns in your behavior. Here you'll see your evolution.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Got it"
    },
    insightsDiscovered: {
      title: "Discover your patterns",
      description: "Here you'll see how you're evolving over time. Emotions, decisions, values - everything becomes clear.\n\nIn the synthesis area you can also add people dear to you - the AI will recognize them as you mention them.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Start exploring"
    },
    valuesDiscovered: {
      title: "Your values",
      description: "What really matters to you? Add at least one value that guides your choices.\n\nE.g.: \"Family\", \"Growth\", \"Freedom\"\n\nYou can always modify and add more over time.",
      icon: <Sparkles size={20} className="text-emerald-500" />,
      ctaText: "Add a value"
    },
    goalsDiscovered: {
      title: "Your goals",
      description: "What are you working towards? Add at least one goal you want to achieve.\n\nE.g.: \"Improve health\", \"New job\"\n\nYou can always modify and add more over time.",
      icon: <TrendingUp size={20} className="text-blue-500" />,
      ctaText: "Add a goal"
    },
    allDiscoveryComplete: {
      title: "",
      description: "",
      icon: null,
      ctaText: ""
    }
  },
  Spanish: {
    pulseIntro: {
      title: "Explora. Crece contigo.",
      description: "Esta app aprende a conocerte día a día. Cuanto más compartes, más inteligente se vuelve. Empecemos juntos este viaje.",
      icon: <Sparkles size={20} className="text-indigo-500" />,
      ctaText: "Empecemos"
    },
    evolutionExplained: {
      title: "Captura un momento",
      description: "Escribe cómo te sientes, qué pasó, o qué estás pensando.\n\nEj: \"Hoy me siento lleno de energía\" o \"Discutí con Marco y me siento confundido\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Escribe algo"
    },
    homeInputDiscovered: {
      title: "Captura un momento",
      description: "Escribe cómo te sientes, qué pasó, o qué estás pensando.\n\nEj: \"Hoy me siento lleno de energía\" o \"Discutí con Marco y me siento confundido\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Escribe algo"
    },
    voiceInputDiscovered: {
      title: "¿Prefieres hablar?",
      description: "Mantén pulsado el micrófono y cuéntame. Tu voz será transcrita automáticamente.",
      icon: <Mic size={20} className="text-rose-500" />,
      ctaText: "Prueba la voz"
    },
    photoInputDiscovered: {
      title: "Añade una foto",
      description: "Una imagen puede contar mucho. Toma o elige una foto para enriquecer tu momento.",
      icon: <Camera size={20} className="text-emerald-500" />,
      ctaText: "Toma una foto"
    },
    archiveIntro: {
      title: "Aquí está tu Diario",
      description: "Todos tus momentos se guardan aquí. Puedes revisarlos, buscarlos y descubrir patrones con el tiempo.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Entendido, continúa"
    },
    journalDiscovered: {
      title: "Tu archivo personal",
      description: "Aquí encontrarás todo lo que has registrado. Ahora descubramos el chat.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Ir al chat"
    },
    consultIntro: {
      title: "Habla conmigo",
      description: "Aquí puedes conversar con la IA que te conoce. Pide consejos, explora decisiones, o simplemente reflexiona juntos.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Entendido"
    },
    chatDiscovered: {
      title: "Tu chat personal",
      description: "Aquí puedes pedir consejos, explorar decisiones y reflexionar junto con la IA que te conoce. Vuelve cuando quieras hablar.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Ir a decisiones"
    },
    decisionsIntro: {
      title: "Tus Decisiones",
      description: "Aquí puedes registrar las elecciones importantes de tu vida. La IA te ayudará a analizarlas.",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Entendido"
    },
    decisionsDiscovered: {
      title: "Tus decisiones",
      description: "Aquí puedes registrar las elecciones importantes. La IA te ayudará a analizarlas cuando lo necesites.\n\nEj: \"¿Debo aceptar el nuevo trabajo?\"",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Continuar"
    },
    insightsIntro: {
      title: "Tus Insights",
      description: "A medida que registras, la IA descubre patrones en tu comportamiento. Aquí verás tu evolución.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Entendido"
    },
    insightsDiscovered: {
      title: "Descubre tus patrones",
      description: "Aquí verás cómo estás evolucionando con el tiempo. Emociones, decisiones, valores - todo se vuelve claro.\n\nEn la zona de síntesis también puedes añadir personas queridas - la IA las reconocerá mientras las mencionas.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Empieza a explorar"
    },
    valuesDiscovered: {
      title: "Tus valores",
      description: "¿Qué es lo que realmente te importa? Añade al menos un valor que guíe tus decisiones.\n\nEj: \"Familia\", \"Crecimiento\", \"Libertad\"\n\nSiempre podrás modificarlos y añadir más con el tiempo.",
      icon: <Sparkles size={20} className="text-emerald-500" />,
      ctaText: "Añade un valor"
    },
    goalsDiscovered: {
      title: "Tus objetivos",
      description: "¿Hacia qué estás trabajando? Añade al menos un objetivo que quieras alcanzar.\n\nEj: \"Mejorar la salud\", \"Nuevo trabajo\"\n\nSiempre podrás modificarlos y añadir más con el tiempo.",
      icon: <TrendingUp size={20} className="text-blue-500" />,
      ctaText: "Añade un objetivo"
    },
    allDiscoveryComplete: {
      title: "",
      description: "",
      icon: null,
      ctaText: ""
    }
  },
  German: {
    pulseIntro: {
      title: "Erkunde. Wächst mit dir.",
      description: "Diese App lernt dich Tag für Tag kennen. Je mehr du teilst, desto intelligenter wird sie. Lass uns diese Reise gemeinsam beginnen.",
      icon: <Sparkles size={20} className="text-indigo-500" />,
      ctaText: "Los geht's"
    },
    evolutionExplained: {
      title: "Erfasse einen Moment",
      description: "Schreibe wie du dich fühlst, was passiert ist, oder worüber du nachdenkst.\n\nZ.B.: \"Heute fühle ich mich voller Energie\" oder \"Ich habe mich mit Marco gestritten und bin verwirrt\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Schreib etwas"
    },
    homeInputDiscovered: {
      title: "Erfasse einen Moment",
      description: "Schreibe wie du dich fühlst, was passiert ist, oder worüber du nachdenkst.\n\nZ.B.: \"Heute fühle ich mich voller Energie\" oder \"Ich habe mich mit Marco gestritten und bin verwirrt\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Schreib etwas"
    },
    voiceInputDiscovered: {
      title: "Lieber sprechen?",
      description: "Halte das Mikrofon gedrückt und erzähle. Deine Stimme wird automatisch transkribiert.",
      icon: <Mic size={20} className="text-rose-500" />,
      ctaText: "Stimme ausprobieren"
    },
    photoInputDiscovered: {
      title: "Füge ein Foto hinzu",
      description: "Ein Bild kann viel erzählen. Mach oder wähle ein Foto, um deinen Moment zu bereichern.",
      icon: <Camera size={20} className="text-emerald-500" />,
      ctaText: "Foto machen"
    },
    archiveIntro: {
      title: "Hier ist dein Tagebuch",
      description: "Alle deine Momente werden hier gespeichert. Du kannst sie durchsehen, durchsuchen und Muster entdecken.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Verstanden, weiter"
    },
    journalDiscovered: {
      title: "Dein persönliches Archiv",
      description: "Hier findest du alles, was du erfasst hast. Jetzt entdecken wir den Chat.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Zum Chat"
    },
    consultIntro: {
      title: "Sprich mit mir",
      description: "Hier kannst du mit der KI chatten, die dich kennt. Frag nach Rat, erkunde Entscheidungen, oder reflektiere einfach zusammen.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Verstanden"
    },
    chatDiscovered: {
      title: "Dein persönlicher Chat",
      description: "Hier kannst du um Rat fragen, Entscheidungen erkunden und gemeinsam mit der KI reflektieren, die dich kennt. Komm zurück, wenn du reden möchtest.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Zu Entscheidungen"
    },
    decisionsIntro: {
      title: "Deine Entscheidungen",
      description: "Hier kannst du die wichtigen Entscheidungen deines Lebens erfassen. Die KI hilft dir bei der Analyse.",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Verstanden"
    },
    decisionsDiscovered: {
      title: "Deine Entscheidungen",
      description: "Hier kannst du wichtige Entscheidungen erfassen. Die KI hilft dir bei der Analyse, wenn du sie brauchst.\n\nZ.B.: \"Soll ich den neuen Job annehmen?\"",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Weiter"
    },
    insightsIntro: {
      title: "Deine Insights",
      description: "Während du erfasst, entdeckt die KI Muster in deinem Verhalten. Hier siehst du deine Entwicklung.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Verstanden"
    },
    insightsDiscovered: {
      title: "Entdecke deine Muster",
      description: "Hier siehst du, wie du dich im Laufe der Zeit entwickelst. Emotionen, Entscheidungen, Werte - alles wird klar.\n\nIm Synthesebereich kannst du auch wichtige Menschen hinzufügen - die KI erkennt sie, wenn du sie erwähnst.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Beginne zu erkunden"
    },
    valuesDiscovered: {
      title: "Deine Werte",
      description: "Was ist dir wirklich wichtig? Füge mindestens einen Wert hinzu, der deine Entscheidungen leitet.\n\nZ.B.: \"Familie\", \"Wachstum\", \"Freiheit\"\n\nDu kannst sie jederzeit ändern und weitere hinzufügen.",
      icon: <Sparkles size={20} className="text-emerald-500" />,
      ctaText: "Wert hinzufügen"
    },
    goalsDiscovered: {
      title: "Deine Ziele",
      description: "Worauf arbeitest du hin? Füge mindestens ein Ziel hinzu, das du erreichen möchtest.\n\nZ.B.: \"Gesundheit verbessern\", \"Neuer Job\"\n\nDu kannst sie jederzeit ändern und weitere hinzufügen.",
      icon: <TrendingUp size={20} className="text-blue-500" />,
      ctaText: "Ziel hinzufügen"
    },
    allDiscoveryComplete: {
      title: "",
      description: "",
      icon: null,
      ctaText: ""
    }
  },
  French: {
    pulseIntro: {
      title: "Explore. Elle grandit avec toi.",
      description: "Cette app apprend à te connaître jour après jour. Plus tu partages, plus elle devient intelligente. Commençons ce voyage ensemble.",
      icon: <Sparkles size={20} className="text-indigo-500" />,
      ctaText: "Commençons"
    },
    evolutionExplained: {
      title: "Capture un moment",
      description: "Écris comment tu te sens, ce qui s'est passé, ou ce à quoi tu penses.\n\nEx: \"Aujourd'hui je me sens plein d'énergie\" ou \"Je me suis disputé avec Marco et je suis confus\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Écris quelque chose"
    },
    homeInputDiscovered: {
      title: "Capture un moment",
      description: "Écris comment tu te sens, ce qui s'est passé, ou ce à quoi tu penses.\n\nEx: \"Aujourd'hui je me sens plein d'énergie\" ou \"Je me suis disputé avec Marco et je suis confus\"",
      icon: <PenLine size={20} className="text-indigo-500" />,
      ctaText: "Écris quelque chose"
    },
    voiceInputDiscovered: {
      title: "Tu préfères parler?",
      description: "Maintiens le micro et raconte. Ta voix sera transcrite automatiquement.",
      icon: <Mic size={20} className="text-rose-500" />,
      ctaText: "Essaie la voix"
    },
    photoInputDiscovered: {
      title: "Ajoute une photo",
      description: "Une image peut raconter beaucoup. Prends ou choisis une photo pour enrichir ton moment.",
      icon: <Camera size={20} className="text-emerald-500" />,
      ctaText: "Prends une photo"
    },
    archiveIntro: {
      title: "Voici ton Journal",
      description: "Tous tes moments sont sauvegardés ici. Tu peux les revoir, les chercher et découvrir des patterns.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Compris, continue"
    },
    journalDiscovered: {
      title: "Ton archive personnel",
      description: "Ici tu trouveras tout ce que tu as enregistré. Maintenant découvrons le chat.",
      icon: <BookOpen size={20} className="text-teal-500" />,
      ctaText: "Aller au chat"
    },
    consultIntro: {
      title: "Parle-moi",
      description: "Ici tu peux discuter avec l'IA qui te connaît. Demande des conseils, explore des décisions, ou réfléchis simplement ensemble.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Compris"
    },
    chatDiscovered: {
      title: "Ton chat personnel",
      description: "Ici tu peux demander des conseils, explorer des décisions et réfléchir avec l'IA qui te connaît. Reviens quand tu veux parler.",
      icon: <MessageSquare size={20} className="text-indigo-500" />,
      ctaText: "Aller aux décisions"
    },
    decisionsIntro: {
      title: "Tes Décisions",
      description: "Ici tu peux enregistrer les choix importants de ta vie. L'IA t'aidera à les analyser.",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Compris"
    },
    decisionsDiscovered: {
      title: "Tes décisions",
      description: "Ici tu peux enregistrer les choix importants. L'IA t'aidera à les analyser quand tu en auras besoin.\n\nEx: \"Dois-je accepter le nouveau travail?\"",
      icon: <Compass size={20} className="text-amber-500" />,
      ctaText: "Continuer"
    },
    insightsIntro: {
      title: "Tes Insights",
      description: "Au fur et à mesure que tu enregistres, l'IA découvre des patterns dans ton comportement. Ici tu verras ton évolution.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Compris"
    },
    insightsDiscovered: {
      title: "Découvre tes patterns",
      description: "Ici tu verras comment tu évolues au fil du temps. Émotions, décisions, valeurs - tout devient clair.\n\nDans la zone synthèse, tu peux aussi ajouter des personnes qui te sont chères - l'IA les reconnaîtra au fur et à mesure que tu les mentionnes.",
      icon: <BarChart3 size={20} className="text-blue-500" />,
      ctaText: "Commence à explorer"
    },
    valuesDiscovered: {
      title: "Tes valeurs",
      description: "Qu'est-ce qui compte vraiment pour toi? Ajoute au moins une valeur qui guide tes choix.\n\nEx: \"Famille\", \"Croissance\", \"Liberté\"\n\nTu pourras toujours les modifier et en ajouter d'autres au fil du temps.",
      icon: <Sparkles size={20} className="text-emerald-500" />,
      ctaText: "Ajoute une valeur"
    },
    goalsDiscovered: {
      title: "Tes objectifs",
      description: "Vers quoi travailles-tu? Ajoute au moins un objectif que tu veux atteindre.\n\nEx: \"Améliorer la santé\", \"Nouveau travail\"\n\nTu pourras toujours les modifier et en ajouter d'autres au fil du temps.",
      icon: <TrendingUp size={20} className="text-blue-500" />,
      ctaText: "Ajoute un objectif"
    },
    allDiscoveryComplete: {
      title: "",
      description: "",
      icon: null,
      ctaText: ""
    }
  }
};

const FeatureDiscovery: React.FC<FeatureDiscoveryProps> = ({
  featureKey,
  discoveryState,
  onDismiss,
  onActivate,
  onNavigate,
  currentView,
  language,
  position = 'bottom',
  showArrow = false,
  arrowDirection = 'down'
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [hasDismissed, setHasDismissed] = useState(false);
  const [dragY, setDragY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const modalRef = useRef<HTMLDivElement>(null);
  const startYRef = useRef(0);
  const velocityRef = useRef(0);
  const lastYRef = useRef(0);
  const lastTimeRef = useRef(0);

  const content = DISCOVERY_CONTENT[language]?.[featureKey] || DISCOVERY_CONTENT.English[featureKey];
  const isAlreadyDiscovered = discoveryState[featureKey] === true;
  const stepConfig = STEP_MAP.get(featureKey);
  const requiresAction = stepConfig?.requiresAction ?? false;

  useEffect(() => {
    setIsVisible(false);
    setIsExiting(false);
    setHasDismissed(false);
    setDragY(0);

    if (!isAlreadyDiscovered && content?.title) {
      const timer = setTimeout(() => setIsVisible(true), 400);
      return () => clearTimeout(timer);
    }
  }, [featureKey, isAlreadyDiscovered, content?.title]);

  // No auto-advance: discovery only completes when user performs the action
  // The modal stays visible until user dismisses it or completes the step

  const handleDismiss = useCallback(() => {
    if (hasDismissed || isExiting) return;
    setHasDismissed(true);
    setIsExiting(true);

    // Smooth exit animation
    setTimeout(() => {
      setIsVisible(false);
      onDismiss(featureKey);
      if (stepConfig?.navigateTo && onNavigate) {
        const navTarget = viewMapping[stepConfig.navigateTo];
        if (navTarget && currentView !== navTarget) {
          onNavigate(navTarget);
        }
      }
    }, 280);
  }, [hasDismissed, isExiting, featureKey, onDismiss, stepConfig, onNavigate, currentView]);

  const handleCTAClick = useCallback(() => {
    if (hasDismissed || isExiting) return;
    setHasDismissed(true);

    if (requiresAction && onActivate) {
      setIsExiting(true);
      setTimeout(() => {
        setIsVisible(false);
        onActivate(featureKey);
      }, 280);
    } else {
      handleDismiss();
    }
  }, [hasDismissed, isExiting, requiresAction, onActivate, featureKey, handleDismiss]);

  // Touch handling for swipe-to-dismiss
  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    if (isExiting) return;
    const touch = e.touches[0];
    startYRef.current = touch.clientY;
    lastYRef.current = touch.clientY;
    lastTimeRef.current = Date.now();
    velocityRef.current = 0;
    setIsDragging(true);
  }, [isExiting]);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!isDragging || isExiting) return;
    const touch = e.touches[0];
    const currentY = touch.clientY;
    const deltaY = currentY - startYRef.current;

    // Calculate velocity for momentum
    const now = Date.now();
    const dt = now - lastTimeRef.current;
    if (dt > 0) {
      velocityRef.current = (currentY - lastYRef.current) / dt;
    }
    lastYRef.current = currentY;
    lastTimeRef.current = now;

    // Only allow dragging down
    if (deltaY > 0) {
      setDragY(deltaY * 0.6); // Add resistance
    }
  }, [isDragging, isExiting]);

  const handleTouchEnd = useCallback(() => {
    if (!isDragging || isExiting) return;
    setIsDragging(false);

    // Check if should dismiss based on drag distance or velocity
    const shouldDismiss = dragY > 80 || velocityRef.current > 0.5;

    if (shouldDismiss) {
      handleDismiss();
    } else {
      // Spring back
      setDragY(0);
    }
  }, [isDragging, isExiting, dragY, handleDismiss]);

  if (isAlreadyDiscovered || !content?.title || !isVisible) {
    return null;
  }

  const overlay = (
    <>
      {/* Backdrop - lighter and smoother */}
      <div
        className={`
          fixed inset-0 z-[99998]
          bg-black/30 backdrop-blur-[2px]
          transition-all duration-300 ease-out
          ${isExiting ? 'opacity-0' : 'opacity-100'}
        `}
        style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
        onClick={handleDismiss}
      />

      {/* Modal Container */}
      <div
        className={`
          fixed z-[99999]
          left-0 right-0 bottom-0
          px-3 pb-[calc(env(safe-area-inset-bottom)+90px)]
          sm:left-4 sm:right-4 sm:bottom-auto sm:top-1/2 sm:-translate-y-1/2 sm:pb-0
        `}
        style={{ pointerEvents: isExiting ? 'none' : 'auto' }}
      >
        <div
          ref={modalRef}
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          className={`
            bg-white/95 backdrop-blur-xl shadow-2xl
            rounded-[24px] sm:rounded-[20px]
            p-5 pt-3 sm:p-5
            mx-auto max-w-md sm:max-w-sm
            border border-white/20
            will-change-transform
          `}
          style={{
            transform: `translateY(${isExiting ? '120%' : dragY}px) scale(${isExiting ? 0.95 : 1})`,
            opacity: isExiting ? 0 : Math.max(0, 1 - dragY / 200),
            transition: isDragging
              ? 'none'
              : 'transform 0.4s cubic-bezier(0.32, 0.72, 0, 1), opacity 0.3s ease-out',
            boxShadow: '0 -4px 32px rgba(0,0,0,0.08), 0 8px 32px rgba(0,0,0,0.12)'
          }}
        >
          {/* Drag handle */}
          <div className="w-10 h-1 bg-black/10 rounded-full mx-auto mb-4 sm:hidden" />

          <div className="flex flex-col sm:flex-row items-center sm:items-start gap-3 text-center sm:text-left">
            {/* Icon */}
            <div className="w-12 h-12 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br from-indigo-50 to-purple-50 flex items-center justify-center shrink-0 shadow-sm border border-indigo-100/30">
              {content.icon}
            </div>

            <div className="flex-1 min-w-0">
              {/* Title */}
              <h3 className="text-[17px] sm:text-[16px] font-bold text-black/90 leading-snug tracking-tight">
                {content.title}
              </h3>

              {/* Description */}
              <div className="text-[14px] sm:text-[13px] text-black/50 leading-relaxed mt-2 whitespace-pre-line">
                {content.description}
              </div>

              {/* CTA Button */}
              <button
                onClick={handleCTAClick}
                className="mt-4 sm:mt-3 w-full sm:w-auto flex items-center justify-center gap-1.5 px-5 py-2.5 sm:py-2 bg-[#1D1D1F] hover:bg-black text-white text-[14px] sm:text-[13px] font-semibold rounded-xl transition-all duration-200 active:scale-[0.97] shadow-lg shadow-black/10"
              >
                {content.ctaText}
                <ChevronRight size={16} className="sm:w-4 sm:h-4 opacity-70" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Custom animation styles */}
      <style>{`
        @keyframes discovery-slide-up {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );

  return createPortal(overlay, document.body);
};

export default FeatureDiscovery;
